<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    content: {
        type: Object,
        required: true,
    },
});

const chartData = ref(null);
const loading = ref(false);
const error = ref(null);

const normalizeId = (rawValue) => {
    const parsed = Number(rawValue);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return null;
    }

    return parsed;
};

const formId = computed(() =>
    normalizeId(props.content?.formId ?? props.content?.form_id)
);
const chartId = computed(() =>
    normalizeId(props.content?.chartId ?? props.content?.chart_id)
);
const selectedFieldUuid = ref('all');

const chartType = computed(() => {
    const configuredType =
        props.content?.chartType ?? props.content?.chart_type;
    if (typeof configuredType === 'string' && configuredType !== '') {
        return configuredType;
    }

    return chartData.value?.chart?.chart_type ?? 'bar';
});

const customColors = computed(() => {
    const configuredColors =
        props.content?.customColors ?? props.content?.custom_colors;

    if (!Array.isArray(configuredColors)) {
        return [];
    }

    return configuredColors
        .filter((color) => typeof color === 'string')
        .map((color) => color.trim())
        .filter((color) => color !== '');
});

const donutCenterValueDisplay = computed(() => {
    const configuredValue =
        props.content?.donutCenterValueDisplay ??
        props.content?.donut_center_value_display;

    if (['actual', 'percentage_and_actual'].includes(configuredValue)) {
        return configuredValue;
    }

    return 'percentage';
});

const belowChartLabel = computed(() => {
    const configuredValue =
        props.content?.belowChartLabel ?? props.content?.below_chart_label;

    return typeof configuredValue === 'string' ? configuredValue.trim() : '';
});

const toNumericValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumericLabel = (value) => {
    return toNumericValue(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
    });
};

const actualValuesLabel = computed(() => {
    if (
        chartType.value !== 'donut' ||
        donutCenterValueDisplay.value !== 'percentage_and_actual' ||
        filteredChartData.value?.chart?.comparison_settings?.enabled !== true
    ) {
        return null;
    }

    const primarySum = toNumericValue(
        filteredChartData.value?.summary?.value_sum
    );
    const comparisonSum = toNumericValue(
        filteredChartData.value?.summary?.comparison_value_sum
    );

    return `${formatNumericLabel(
        primarySum
    )}<span style="opacity: 0.8"> / ${formatNumericLabel(
        primarySum + comparisonSum
    )}</span>`;
});

const pageBuilderActive = computed(
    () => window.myflow?.pagebuilderActive === true
);
const fieldSelectorEnabled = computed(() => {
    return (
        props.content?.fieldSelectorEnabled === true ||
        props.content?.field_selector_enabled === true
    );
});

const renderableFields = computed(() => {
    if (!Array.isArray(chartData.value?.fields)) {
        return [];
    }

    return chartData.value.fields.filter((field) => {
        return (
            field?.exists !== false &&
            typeof field?.field_uuid === 'string' &&
            field.field_uuid !== ''
        );
    });
});

const fieldOptions = computed(() => {
    const allOption = {
        label: _mft('pagebuilderBlock:formChart.fieldSelector.allFields'),
        value: 'all',
    };

    if (renderableFields.value.length === 0) {
        return [allOption];
    }

    const options = renderableFields.value.map((field) => ({
        label: field.title || field.field_uuid,
        value: field.field_uuid,
    }));

    return [allOption, ...options];
});

const selectedFieldIndex = computed(() => {
    if (selectedFieldUuid.value === 'all') {
        return null;
    }

    const index = renderableFields.value.findIndex(
        (field) => field?.field_uuid === selectedFieldUuid.value
    );

    return index >= 0 ? index : null;
});

const filterArrayBySelectedField = (items) => {
    if (!Array.isArray(items) || selectedFieldIndex.value === null) {
        return items;
    }

    return items.filter((item, index) => {
        return (
            item?.field_uuid === selectedFieldUuid.value ||
            index === selectedFieldIndex.value
        );
    });
};

const filterSeriesBySelectedField = (series) => {
    if (!Array.isArray(series) || selectedFieldIndex.value === null) {
        return series;
    }

    if (
        chartType.value === 'donut' &&
        chartData.value?.chart?.comparison_settings?.enabled === true
    ) {
        const fieldCount = renderableFields.value.length;

        if (fieldCount <= 0) {
            return series;
        }

        return series.filter(
            (value, index) => index % fieldCount === selectedFieldIndex.value
        );
    }

    if (chartType.value === 'donut') {
        return series.filter(
            (value, index) => index === selectedFieldIndex.value
        );
    }

    return series.map((entry) => {
        if (!entry || !Array.isArray(entry.data)) {
            return entry;
        }

        return {
            ...entry,
            data: entry.data.filter(
                (value, index) => index === selectedFieldIndex.value
            ),
        };
    });
};

const filterLabelsBySelectedField = (labels) => {
    if (!Array.isArray(labels) || selectedFieldIndex.value === null) {
        return labels;
    }

    if (
        chartType.value === 'donut' &&
        chartData.value?.chart?.comparison_settings?.enabled === true
    ) {
        const fieldCount = renderableFields.value.length;

        if (fieldCount <= 0) {
            return labels;
        }

        return labels.filter(
            (label, index) => index % fieldCount === selectedFieldIndex.value
        );
    }

    return labels.filter((label, index) => index === selectedFieldIndex.value);
};

const filteredChartData = computed(() => {
    if (
        !fieldSelectorEnabled.value ||
        selectedFieldUuid.value === 'all' ||
        !chartData.value ||
        selectedFieldIndex.value === null
    ) {
        return chartData.value;
    }

    const data = chartData.value.data ?? {};
    const filteredData = {
        ...data,
        labels: filterLabelsBySelectedField(data.labels),
        series: filterSeriesBySelectedField(data.series),
        distribution: filterArrayBySelectedField(data.distribution),
        points: filterArrayBySelectedField(data.points),
        rows: filterArrayBySelectedField(data.rows),
    };
    const filteredFields = filterArrayBySelectedField(chartData.value.fields);
    const selectedDistribution = Array.isArray(filteredData.distribution)
        ? filteredData.distribution
        : [];
    const selectedRows = Array.isArray(filteredData.rows)
        ? filteredData.rows
        : [];
    const primaryValues =
        selectedDistribution.length > 0 ? selectedDistribution : selectedRows;

    return {
        ...chartData.value,
        chart: {
            ...chartData.value.chart,
            field_uuids: Array.isArray(chartData.value.chart?.field_uuids)
                ? chartData.value.chart.field_uuids.filter(
                      (fieldUuid) => fieldUuid === selectedFieldUuid.value
                  )
                : chartData.value.chart?.field_uuids,
        },
        fields: filteredFields,
        summary: {
            ...chartData.value.summary,
            value_count: primaryValues.reduce(
                (total, item) => total + Number(item?.count || 0),
                0
            ),
            value_sum: primaryValues.reduce(
                (total, item) => total + Number(item?.sum || 0),
                0
            ),
            comparison_value_sum:
                chartType.value === 'donut' &&
                chartData.value?.chart?.comparison_settings?.enabled === true &&
                Array.isArray(filteredData.series)
                    ? Number(filteredData.series[1] || 0)
                    : chartData.value.summary?.comparison_value_sum,
        },
        data: filteredData,
    };
});

const filteredCustomColors = computed(() => {
    if (
        !fieldSelectorEnabled.value ||
        selectedFieldUuid.value === 'all' ||
        selectedFieldIndex.value === null ||
        customColors.value.length === 0
    ) {
        return customColors.value;
    }

    return [
        customColors.value[
            selectedFieldIndex.value % customColors.value.length
        ],
    ];
});

const loadChart = async () => {
    chartData.value = null;
    error.value = null;

    if (!formId.value || !chartId.value) {
        return;
    }

    loading.value = true;

    try {
        const res = await axios.get(
            route('api.form.chart.viewer-data', {
                id: formId.value,
                chartId: chartId.value,
            })
        );
        chartData.value = res.data;
    } catch (e) {
        if (pageBuilderActive.value) {
            error.value = _mft('pagebuilderBlock:formChart.loadError');
        }
    } finally {
        loading.value = false;
    }
};

watch(
    [formId, chartId],
    async () => {
        await loadChart();
    },
    { immediate: true }
);

watch([fieldSelectorEnabled, fieldOptions], () => {
    if (
        !fieldSelectorEnabled.value ||
        !fieldOptions.value.some(
            (option) => option.value === selectedFieldUuid.value
        )
    ) {
        selectedFieldUuid.value = 'all';
    }
});
</script>

<template>
    <div>
        <div v-if="loading" class="flex justify-center">
            <mf-spinner-medium class="w-24 h-12" />
        </div>

        <p v-else-if="!formId || !chartId" class="text-sm text-gray-500">
            <template v-if="pageBuilderActive">
                {{ _mft('pagebuilderBlock:formChart.selectChartHint') }}
            </template>
        </p>

        <p v-else-if="error" class="text-sm text-gray-500">{{ error }}</p>

        <div v-else-if="chartData" class="space-y-3">
            <form-chart-visualization
                v-if="chartType !== 'table'"
                :chart-type="chartType"
                :payload="filteredChartData"
                :custom-colors="filteredCustomColors"
                :donut-center-value-display="donutCenterValueDisplay"
            />

            <form-chart-data-table
                v-if="chartType === 'table'"
                :chart-type="chartType"
                :payload="filteredChartData"
            />

            <div>
                <div
                    v-if="chartType === 'donut' && belowChartLabel"
                    class="text-center text-md font-bold"
                >
                    {{ belowChartLabel }}
                </div>

                <div
                    v-if="actualValuesLabel !== null"
                    class="text-center text-sm font-medium"
                    v-html="actualValuesLabel"
                />
            </div>

            <div
                v-if="fieldSelectorEnabled"
                class="max-w-sm mx-auto flex justify-center text-center"
            >
                <input-select
                    v-model:content="selectedFieldUuid"
                    :options="fieldOptions"
                />
            </div>
        </div>
    </div>
</template>
